// Tablero ejecutivo en una pantalla (artboard e): cifras, cumplimiento por
// área contra la meta, vencidos escalados, SLA por categoría, tickets por
// estado y riesgo previsto. Para Dirección, gerencias y jefaturas.
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Boton, EsqueletoTabla, PuntoNaranja, Semaforo, TituloPagina, Vacio } from "@iavmx/ui";
import { useTablero, type Periodo } from "./datos";
import { BarraMeta, BotonNuevoTicket, Cifra, PorConfirmar, Segmento, Seccion, SemaforoTicket } from "./comunes";
import { estadoLabel, fechaCorta, folio, pct, slaTexto } from "./formato";

const ETIQUETA_PERIODO: Record<Periodo, string> = { hoy: "Hoy", semana: "Esta semana", mes: "Este mes" };

export const SUPUESTOS = [
  "Meta de cumplimiento de SLA: 90 %.",
  "SLA de referencia del PID: arte 24 h, video 5 días, nota 2 días, dato 3 días, soporte 24 h, permiso 2 días; horas corridas, no hábiles.",
  "Prioridad: urgente si el mensaje lo dice o la fecha es en menos de 48 h; alta si es en menos de una semana. La cambia Estrategia Digital o la jefatura del área.",
  "Asigna la jefatura del área destino o Estrategia Digital; la IA solo sugiere por carga e historial.",
  "Al vencer, el ticket se escala a quien diga el SLA, si no a la jefatura del área asignada, si no a Estrategia Digital.",
  "Entregado se cierra solo a los 3 días si nadie pide cambios.",
  "Ámbar cuando se consumió el 75 % del SLA (o las horas_ambar configuradas).",
  "Ve todos los tickets: Dirección, gerencias y Estrategia Digital; los demás, los de su área y los propios.",
];

/** Un tiempo como cifra grande: número y unidad como sufijo, nunca una frase en 5xl. */
function cifraHoras(h: number | null): { valor: React.ReactNode; sufijo: string } {
  if (h === null) return { valor: "—", sufijo: "" };
  if (h < 1) return { valor: "<1", sufijo: " h" };
  if (h < 48) return { valor: Math.round(h * 10) / 10, sufijo: " h" };
  const dias = Math.round((h / 24) * 10) / 10;
  return { valor: dias, sufijo: dias === 1 ? " día" : " días" };
}

export function Tablero() {
  const [periodo, setPeriodo] = React.useState<Periodo>("semana");
  const q = useTablero(periodo);
  const accion = (
    <div className="flex flex-wrap items-center gap-3">
      <Segmento
        etiqueta="Periodo"
        valor={periodo}
        onChange={setPeriodo}
        opciones={[
          { valor: "hoy", texto: "Hoy" },
          { valor: "semana", texto: "Esta semana" },
          { valor: "mes", texto: "Mes" },
        ]}
      />
      <BotonNuevoTicket />
    </div>
  );
  if (q.error)
    return (
      <>
        <TituloPagina accion={accion}>
          Semáforo interdepartamental
          <PuntoNaranja />
        </TituloPagina>
        <Vacio
          titulo="Este tablero es para Dirección, gerencias y jefaturas"
          texto={q.error.message}
          accion={
            <Boton variante="contorno" asChild>
              <Link to="/tickets">Ir a mis tickets</Link>
            </Boton>
          }
        />
      </>
    );
  const d = q.data;
  const ahora = d ? new Date(d.ahora) : new Date();
  const desde = d ? new Date(d.periodo.desde) : null;
  return (
    <>
      <TituloPagina
        accion={accion}
        descripcion={
          d
            ? `${ETIQUETA_PERIODO[periodo]} · desde el ${fechaCorta(desde)} · ${d.areas} áreas, ${d.personas} personas. Se recalcula cada mañana a las 9:00 y al abrir esta página.`
            : "Cargando el tablero…"
        }
      >
        Semáforo interdepartamental
        <PuntoNaranja />
      </TituloPagina>
      {!d ? (
        <EsqueletoTabla filas={6} />
      ) : (
        <div className="flex min-w-0 flex-col gap-8">
          <div className="grid grid-cols-2 gap-x-6 border-y border-t-tinta border-b-linea lg:grid-cols-4">
            <Cifra etiqueta="Tickets abiertos" valor={d.cifras.abiertos} nota={`${d.cifras.nuevosEnPeriodo} ${d.cifras.nuevosEnPeriodo === 1 ? "nuevo" : "nuevos"} en el periodo`} />
            <Cifra
              etiqueta="Vencidos"
              valor={d.cifras.vencidos}
              tono={d.cifras.vencidos > 0 ? "rojo" : undefined}
              nota={
                d.cifras.areaConMasVencidos ? (
                  <Semaforo color="rojo">
                    {d.cifras.areaConMasVencidos.n} en {d.cifras.areaConMasVencidos.area}
                  </Semaforo>
                ) : (
                  "ninguno escalado"
                )
              }
            />
            <Cifra
              etiqueta="Cumplimiento de SLA"
              valor={d.cifras.cumplimiento ?? "—"}
              sufijo={d.cifras.cumplimiento === null ? undefined : "%"}
              tono={d.cifras.cumplimiento === null ? undefined : d.cifras.cumplimiento >= d.meta ? "verde" : d.cifras.cumplimiento < 70 ? "rojo" : "ambar"}
              nota={
                <>
                  meta {d.meta} % <PorConfirmar que="Meta de cumplimiento" /> ·{" "}
                  {d.cifras.cumplimientoAnterior === null ? "sin periodo anterior" : `periodo anterior ${d.cifras.cumplimientoAnterior} %`} ·{" "}
                  {d.cifras.entregasEnPeriodo} {d.cifras.entregasEnPeriodo === 1 ? "entrega" : "entregas"}
                </>
              }
            />
            <Cifra etiqueta="De Nuevo a Asignado" {...cifraHoras(d.cifras.tiempoMedioAsignacionHoras)} nota="tiempo medio en el periodo" />
          </div>

          <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="flex min-w-0 flex-col gap-8">
              <Seccion titulo="Cumplimiento por área" extra={`Entregas dentro del SLA · la línea es la meta de ${d.meta} %`}>
                {d.porArea.length === 0 ? (
                  <Vacio titulo="Sin actividad en el periodo" texto="Cuando haya tickets asignados o entregados, cada área aparece aquí con su semáforo." />
                ) : (
                  <>
                  {/* En celular, una lista: la tabla escondería vencidos y cumplimiento tras un scroll que nadie ve. */}
                  <ul className="flex flex-col divide-y divide-linea border-t border-tinta md:hidden">
                    {d.porArea.map((a) => (
                      <li key={a.area.id} className="flex flex-col gap-2 py-3 font-ui text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <Link to="/area" search={{ area: a.area.clave }} className="font-semibold hover:underline">
                            {a.area.nombre}
                          </Link>
                          {a.semaforo ? (
                            <Semaforo color={a.semaforo}>{a.semaforo === "ambar" ? "Ámbar" : a.semaforo === "rojo" ? "Rojo" : "Verde"}</Semaforo>
                          ) : (
                            <Semaforo color="gris">Sin entregas</Semaforo>
                          )}
                        </div>
                        <BarraMeta pct={a.cumplimiento} meta={d.meta} />
                        <span className="text-tinta-2">
                          {a.cumplimiento === null ? "Sin entregas en el periodo" : `${a.cumplimiento} % en SLA`} · {a.abiertos} {a.abiertos === 1 ? "abierto" : "abiertos"} ·{" "}
                          <span className={a.vencidos > 0 ? "font-semibold text-rojo" : ""}>
                            {a.vencidos} {a.vencidos === 1 ? "vencido" : "vencidos"}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[560px] font-ui text-sm">
                      <thead>
                        <tr className="border-b border-tinta text-left text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">
                          <th className="py-2 pr-3 font-semibold">Área</th>
                          <th className="py-2 pr-3 font-semibold">Semáforo</th>
                          <th className="w-1/3 py-2 pr-3 font-semibold">Cumplimiento</th>
                          <th className="py-2 pr-3 text-right font-semibold">%</th>
                          <th className="py-2 pr-3 text-right font-semibold">Abiertos</th>
                          <th className="py-2 text-right font-semibold">Vencidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.porArea.map((a) => (
                          <tr key={a.area.id} className="border-b border-linea">
                            <td className="py-3 pr-3 font-semibold">
                              <Link to="/area" search={{ area: a.area.clave }} className="hover:underline">
                                {a.area.nombre}
                              </Link>
                            </td>
                            <td className="py-3 pr-3">
                              {a.semaforo ? (
                                <Semaforo color={a.semaforo}>{a.semaforo === "ambar" ? "Ámbar" : a.semaforo === "rojo" ? "Rojo" : "Verde"}</Semaforo>
                              ) : (
                                <Semaforo color="gris">Sin entregas</Semaforo>
                              )}
                            </td>
                            <td className="py-3 pr-3">
                              <BarraMeta pct={a.cumplimiento} meta={d.meta} />
                            </td>
                            <td className="py-3 pr-3 text-right font-semibold tabular-nums">{a.cumplimiento ?? "—"}</td>
                            <td className="py-3 pr-3 text-right tabular-nums">{a.abiertos}</td>
                            <td className={`py-3 text-right tabular-nums ${a.vencidos > 0 ? "font-semibold text-rojo" : ""}`}>{a.vencidos}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </Seccion>

              <div className="grid gap-8 md:grid-cols-2">
                <Seccion titulo="SLA por categoría">
                  <div className="border-t border-tinta font-ui text-sm">
                    {d.slaPorCategoria.map((s) => (
                      <div key={s.categoria.id} className="flex items-center justify-between gap-3 border-b border-linea py-2">
                        <span>
                          {s.categoria.nombre}
                          {s.abiertos > 0 && <span className="text-tinta-tenue"> · {s.abiertos} abiertos</span>}
                        </span>
                        <span className="shrink-0 tabular-nums text-tinta-2">
                          {slaTexto(s.horasNormal)}
                          {s.cumplimiento !== null && <span className="text-tinta-tenue"> · {pct(s.cumplimiento)}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="font-ui text-xs text-tinta-tenue">
                    SLA de referencia del PID, en horas corridas. <PorConfirmar que="SLA por categoría" />
                  </p>
                </Seccion>
                <Seccion titulo="Tickets por estado">
                  <div className="border-t border-tinta font-ui text-sm">
                    {(Object.keys(d.porEstado) as (keyof typeof d.porEstado)[]).map((e) => (
                      <div key={e} className="flex items-center justify-between gap-3 border-b border-linea py-2">
                        <span>
                          {estadoLabel(e)}
                          {e === "nueva" && <span className="text-tinta-tenue"> · sin asignar</span>}
                          {e === "entregada" && <span className="text-tinta-tenue"> · por cerrar</span>}
                          {e === "cerrada" && <span className="text-tinta-tenue"> · en el periodo</span>}
                        </span>
                        <span className="tabular-nums text-tinta-2">{d.porEstado[e]}</span>
                      </div>
                    ))}
                  </div>
                </Seccion>
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-8">
              <Seccion titulo="Tickets vencidos" extra="Escalados a la jefatura al vencer">
                {d.escalados.length === 0 ? (
                  <p className="font-ui text-sm text-tinta-tenue">Ninguno. Todo lo abierto va dentro de su SLA.</p>
                ) : (
                  <ul className="flex flex-col divide-y divide-linea">
                    {d.escalados.map((t) => (
                      <li key={t.id} className="flex flex-col gap-1 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <Link to="/tickets/$id" params={{ id: t.id }} className="font-semibold hover:underline">
                            {t.titulo}
                          </Link>
                          <SemaforoTicket ticket={t} ahora={ahora} />
                        </div>
                        <span className="font-ui text-xs text-tinta-tenue">
                          {folio(t.folio)} · {t.areaAsignada?.nombre ?? "sin área"} · {t.asignada?.nombre ?? "sin asignar"} · pidió {t.areaSolicitante?.nombre ?? t.solicitante?.nombre ?? "—"}
                          {t.escaladaA && ` · escalado a ${t.escaladaA.nombre}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Seccion>
              <div className="flex flex-col gap-3 bg-fondo-suave p-5">
                <span className="font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">Riesgo previsto · sin IA</span>
                {d.riesgo.length === 0 ? (
                  <p className="font-ui text-sm text-tinta-2">Ningún ticket abierto va más atrasado que su SLA. El cálculo es heurístico: tiempo consumido contra avance y carga de la persona.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {d.riesgo.map((t) => (
                      <li key={t.id} className="flex flex-col gap-1 font-ui text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <Link to="/tickets/$id" params={{ id: t.id }} className="font-semibold hover:underline">
                            {t.titulo}
                          </Link>
                          <span className="tabular-nums text-tinta-2">{Math.round(t.riesgo * 100)} %</span>
                        </div>
                        <div className="h-1.5 w-full bg-fondo-alzado">
                          <div className={t.riesgo >= 0.7 ? "h-1.5 bg-rojo" : "h-1.5 bg-ambar"} style={{ width: `${Math.round(t.riesgo * 100)}%` }} />
                        </div>
                        <span className="text-xs text-tinta-tenue">
                          {estadoLabel(t.estado)} · {t.asignada?.nombre ?? "sin asignar"} · vence {fechaCorta(t.venceEn)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <details className="border border-linea p-4 font-ui text-sm">
                <summary className="cursor-pointer font-semibold">Supuestos por confirmar con Estrategia Digital</summary>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-tinta-2">
                  {SUPUESTOS.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
